import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Heart, MessageCircle, MapPin, BarChart3, Settings, Activity, HeartHandshake } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { useHealth } from '../contexts/HealthContext';
import { useChat } from '../contexts/ChatContext';
import { usePendingMatches } from '../contexts/PendingMatchesContext';
import NotificationBadge from './ui/NotificationBadge';

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { colors } = useTheme();
  const { shouldShowFeature } = useUserPreferences();
  const { hasPendingMedications, hasOverdueScreening } = useHealth();
  const { totalUnreadCount } = useChat();
  const { pendingCount } = usePendingMatches();

  const allNavItems = [
    {
      path: '/grid',
      icon: Heart
    },
    {
      path: '/messages',
      icon: MessageCircle
    },
    {
      path: '/cruise',
      icon: MapPin,
      requiresFeature: 'cruising'
    },
    {
      path: '/health',
      icon: HeartHandshake,
      requiresFeature: 'health'
    },
    {
      path: '/activity',
      icon: Activity,
      requiresFeature: 'activityTracking'
    },
    {
      path: '/settings',
      icon: Settings
    }
  ];

  // Filter nav items based on user preferences
  const navItems = allNavItems.filter(item => 
    !item.requiresFeature || shouldShowFeature(item.requiresFeature as any)
  );

  const isActive = (path: string) => {
    return location.pathname === path || 
           (path === '/grid' && location.pathname === '/') ||
           (path === '/health' && location.pathname === '/health') ||
           (path === '/activity' && location.pathname === '/activity');
  };

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-40"
      style={{ 
        backgroundColor: colors.background,
        borderTop: `1px solid ${colors.border}`
      }}
    >
      <div className="flex justify-around items-center h-16">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const hasHealthReminder = item.path === '/health' && (hasPendingMedications() || hasOverdueScreening());
          const hasMessageBadge = item.path === '/messages' && (totalUnreadCount > 0 || pendingCount > 0);
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`nav-item flex items-center justify-center flex-1 h-full relative ${active ? 'active' : ''}`}
              style={{ 
                color: active ? colors.primary : colors.textTertiary 
              }}
            >
              <Icon size={26} />
              {hasHealthReminder && (
                <div 
                  className="absolute -top-1 -right-1 w-3 h-3 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#FF4757' }}
                >
                  <div 
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{ backgroundColor: 'white' }}
                  />
                </div>
              )}
              {hasMessageBadge && (
                <NotificationBadge count={totalUnreadCount + pendingCount} />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default Navigation;